import { useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useUserProfile } from '@/states/user-profile';
import { toast_success } from '@/libs/toast-wrappers';
import { postLicenseRequest } from '@/api/licenses';
import { errorlog } from '@/libs/error-logs';
import {
  SUCCESS_LICENSE_REQUESTED,
  ERROR_FILE_UPLOAD_REQUIRED,
  ERROR_FILE_MUST_BE_PDF,
} from '@/utils/messages';
import { US_STATES_ARRAY } from '@/libs/us-states';
import { requiredString } from '@/utils/zod-schemas';

import { InputField } from '@/components/form/input-field';

import { SelectField } from '../form/select-field';
import { Button } from '../ui/button';

const schema = z.object({
  license: requiredString,
  state: requiredString,
  expiration: requiredString,
  file: z
    .custom<FileList>(
      (val) => val instanceof FileList && val.length > 0,
      ERROR_FILE_UPLOAD_REQUIRED
    )
    .refine((files) => files[0]?.type === 'application/pdf', ERROR_FILE_MUST_BE_PDF),
});

type InputTypes = z.infer<typeof schema>;

type NewLicenseFormProps = {
  onSuccessSave: () => void;
};

export function NewLicenseForm(props: NewLicenseFormProps) {
  const { onSuccessSave } = props;
  const [saving, setSaving] = useState(false);
  const { profile } = useUserProfile((state) => ({ profile: state.profile }));

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InputTypes>({ resolver: zodResolver(schema) });

  const { t } = useTranslation();

  const TODAY = new Date().toISOString().split('T')[0];

  const onSaveNewLicense: SubmitHandler<InputTypes> = async (data) => {
    if (!profile?.email) return;

    try {
      const license_file = data.file[0];

      setSaving(true);

      await postLicenseRequest({
        email: profile.email,
        country: profile.country ?? '',
        state: data.state,
        license: data.license,
        expiration: data.expiration,
        license_file,
      }).then((response) => {
        if (response) {
          toast_success(SUCCESS_LICENSE_REQUESTED);
          onSuccessSave();
        }
      });
    } catch (error) {
      errorlog(error as Error, 'src/components/profile/new-license-form.tsx');
    }
    setSaving(false);
  };

  return (
    <form className="grid grid-cols-3 gap-4" onSubmit={handleSubmit(onSaveNewLicense)}>
      <InputField
        required
        disabled={saving}
        label={t('profile-license-code')}
        errorMessage={errors.license?.message}
        {...register('license')}
      />
      <SelectField
        required
        enableEmptyOption
        disabled={saving}
        label={t('profile-license-state')}
        options={US_STATES_ARRAY}
        errorMessage={errors.state?.message}
        {...register('state')}
      />
      <InputField
        required
        disabled={saving}
        label={t('profile-license-exp')}
        type="date"
        min={TODAY}
        errorMessage={errors.expiration?.message}
        {...register('expiration')}
      />
      <div className="col-span-2 flex flex-col gap-1">
        <input required type="file" accept="application/pdf" {...register('file')} />
        {errors.file && <span className="text-destructive text-sm">{errors.file.message}</span>}
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {t('submit')}
          <Save />
        </Button>
      </div>
    </form>
  );
}
